import {Children, CSSProperties, isValidElement, ReactNode} from 'react'
import Papa from 'papaparse'

export type DopamineTableCell = {
  content: ReactNode
  text: string
  align?: CSSProperties['textAlign']
}

export type DopamineTableRow = {id: number; cells: DopamineTableCell[]}
export type DopamineTableData = {
  headers: DopamineTableCell[]
  rows: DopamineTableRow[]
}

type TableElementProps = {
  children?: ReactNode
  style?: CSSProperties
}

const getText = (content: ReactNode): string =>
  Children.toArray(content)
    .map(child => {
      if (!isValidElement<TableElementProps>(child)) return String(child)
      return child.type === 'br' ? '\n' : getText(child.props.children)
    })
    .join('')

const getElements = (content: ReactNode) =>
  Children.toArray(content).filter(isValidElement<TableElementProps>)

// Keep the rendered cell content as well as plain text, so links and email
// navigation work identically in the conversation and the expanded table.
export const getDopamineTableData = (
  children: ReactNode,
): DopamineTableData => {
  const sections = getElements(children)
  const getRows = (tag: string): DopamineTableRow[] =>
    getElements(sections.find(section => section.type === tag)?.props.children)
      .filter(row => row.type === 'tr')
      .map((row, id) => ({
        id,
        cells: getElements(row.props.children).map(cell => ({
          content: cell.props.children,
          text: getText(cell.props.children).trim(),
          align: cell.props.style?.textAlign,
        })),
      }))

  return {headers: getRows('thead')[0]?.cells ?? [], rows: getRows('tbody')}
}

export const getDopamineTableNumber = (text: string): number | undefined => {
  if (!/^-?(?:\d+|[1-9]\d{0,2}(?:,\d{3})+)(?:\.\d+)?$/.test(text)) {
    return undefined
  }
  const value = Number(text.replaceAll(',', ''))
  return Number.isFinite(value) ? value : undefined
}

const getCsvValue = (text: string): string | number => {
  const value = getDopamineTableNumber(text)
  // Preserve identifiers, leading zeroes and precision. Only convert ordinary
  // numbers; Papa escapes formula-like text and quotes commas/newlines.
  if (
    value === undefined ||
    /^-?0\d/.test(text) ||
    text.replace(/\D/g, '').length > 15
  ) {
    return text
  }
  return value
}

export const createDopamineTableCsv = (data: DopamineTableData): string =>
  '\uFEFF' +
  Papa.unparse(
    [
      data.headers.map(header => header.text),
      ...data.rows.map(row => row.cells.map(cell => getCsvValue(cell.text))),
    ],
    {escapeFormulae: /^\s*[=+\-@]|^[\t\r]/},
  )

export const getDopamineTableFilename = (
  question: string,
  tableNumber: number,
  date = new Date(),
): string => {
  const title =
    question
      .toLowerCase()
      .replace(/[^a-z\d]+/g, '-')
      .slice(0, 70)
      .replace(/^-|-$/g, '') || 'table'
  const day = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
  const suffix = tableNumber > 1 ? `-table-${tableNumber}` : ''
  return `dopamine-${title}-${day}${suffix}.csv`
}

export const downloadDopamineTable = (
  data: DopamineTableData,
  filename: string,
): void => {
  const url = URL.createObjectURL(
    new Blob([createDopamineTableCsv(data)], {type: 'text/csv;charset=utf-8;'}),
  )
  const link = document.createElement('a')
  try {
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
  } finally {
    link.remove()
    // Let the browser start reading the download before releasing the Blob.
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}
